import React, { FC, useMemo } from 'react';
import { SubscriptionRowStyled } from './SubscriptionRow.style';
import { numeralDeclension } from '../../../utils';
import { FormattedMessage, useIntl } from 'react-intl';
import { SupportedCurrencies } from '../../../redux/specialists/types';
import { SupportedCurrency } from '@mindly/ui-components';
import { priceNormalize } from '@mindly/ui-components';

type SubscriptionRowType = {
  isActive: boolean;
  monthInterval: number;
  discountObject?: { [key: number]: number };
  sessionsPerWeek?: number | null;
  sessionPrice: number;
  currency: SupportedCurrencies;
  onClickHandler: (monthInterval: number) => void;
};

const SubscriptionRow: FC<SubscriptionRowType> = ({
  isActive,
  monthInterval,
  discountObject,
  sessionsPerWeek,
  sessionPrice,
  currency,
  onClickHandler,
}) => {
  const intl = useIntl();

  const sessionCount = useMemo(() => {
    const monthLength = 4;
    return monthInterval * monthLength * (sessionsPerWeek ?? 1);
  }, [monthInterval, sessionsPerWeek]);

  const countOfSessionByMonth = useMemo(() => {
    return `${sessionCount} ${numeralDeclension(sessionCount, [
      intl.formatMessage({
        id: 'other.sessions.one',
        defaultMessage: 'сеанс',
      }),
      intl.formatMessage({
        id: 'other.sessions.many',
        defaultMessage: 'сеанси',
      }),
      intl.formatMessage({
        id: 'other.sessions.several',
        defaultMessage: 'сеансів',
      }),
    ])}`;
  }, [sessionsPerWeek, monthInterval, sessionCount, intl]);

  const discount = useMemo(
    () => discountObject?.[sessionCount] ?? 0,
    [discountObject, sessionsPerWeek, monthInterval, sessionCount]
  );

  const discountPerSession = useMemo(
    () => (discount ? sessionPrice * discount : sessionPrice),
    [sessionPrice, discount]
  );

  const costWithDiscount = useMemo(() => {
    return discount ? (sessionPrice - discountPerSession) * sessionCount : sessionPrice * sessionCount;
  }, [sessionPrice, discount, discountPerSession, sessionCount]);

  return (
    <>
      <SubscriptionRowStyled
        className={`month with-border ${isActive ? 'active' : ''}`}
        onClick={() => onClickHandler(monthInterval)}
      >
        {countOfSessionByMonth}
      </SubscriptionRowStyled>
      <SubscriptionRowStyled
        className={`discount with-border ${isActive ? 'active' : ''}`}
        onClick={() => onClickHandler(monthInterval)}
      >
        <div className={'row-desc'}>
          <FormattedMessage id={'new.specialist.discount'} defaultMessage={'Знижка'} />
        </div>
        {discount ? `-${Math.floor(discount * 100)}%` : '0%'}
      </SubscriptionRowStyled>
      <SubscriptionRowStyled
        className={`price ${isActive ? 'active' : ''}`}
        onClick={() => onClickHandler(monthInterval)}
      >
        {priceNormalize({
          price: costWithDiscount,
          currency: currency as unknown as SupportedCurrency,
          extendedUsd: true,
        })}
        {discount ? (
          <div className={'total-price'}>
            {priceNormalize({
              price: sessionPrice * sessionCount,
              currency: currency as unknown as SupportedCurrency,
              extendedUsd: true,
            })}
          </div>
        ) : null}
      </SubscriptionRowStyled>
    </>
  );
};

export default SubscriptionRow;
